import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, Icon, Skeleton, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../../lib/api-errors";
import { startDownload } from "../../../../lib/start-download";
import { usePermissions } from "../../../../session/permissions";
import { useApi } from "../../../../session/session-provider";
import { useMe } from "../../../../session/use-me";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { useFormatters, wsKeys, type DataExportOut } from "../../shared";
import { useDataExports, useInvalidate } from "../use-settings";
import { Gate, useCanManage } from "./panel-kit";

const ACTIVE: ReadonlySet<string> = new Set(["queued", "running"]);

/**
 * The list never carries download links (each is a short-lived presigned URL): the link is asked for
 * when the user clicks, so it cannot have expired while the page sat open.
 */
function useExportDownload() {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  return useMutation({
    mutationFn: (exportId: string) => unwrap(api.GET("/v1/data-exports/{export_id}", { params: { path: { export_id: exportId } } })),
    onSuccess: (detail) => {
      if (detail.download_url) startDownload(detail.download_url);
      else toast.error(t("settings.privacy.exportExpired"));
    },
    onError: (caught) => toast.error(message(caught)),
  });
}

const isExpired = (item: DataExportOut) => item.expires_at !== null && new Date(item.expires_at).getTime() <= Date.now();

function ExportStatus({ item }: { item: DataExportOut }) {
  const { t } = useTranslation();
  const format = useFormatters();
  const download = useExportDownload();
  if (item.status === "ready" && isExpired(item)) {
    return (
      <Badge tone="neutral">
        <Icon name="clock" className="size-3" />
        {t("settings.privacy.exportExpired")}
      </Badge>
    );
  }
  if (item.status === "ready") {
    return (
      <Button variant="ghost" size="sm" loading={download.isPending} onClick={() => { download.mutate(item.id); }}>
        <Icon name="download" className="size-3.5" />
        {t("settings.privacy.exportDownload", { date: format.date(item.created_at) })}
      </Button>
    );
  }
  if (item.status === "failed") {
    return (
      <Badge tone="danger">
        <Icon name="x-circle" className="size-3" />
        {t("settings.privacy.exportFailed")}
      </Badge>
    );
  }
  return (
    <Badge tone="warning">
      <Icon name="clock" className="size-3" />
      {t("settings.privacy.exportRunning")}
    </Badge>
  );
}

/** "Export all data" → ZIP prepared in the background; the latest export's status / link. */
export function DataExportCard() {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const me = useMe();
  const permitted = usePermissions().can("data.export");
  const manage = useCanManage("data.export");
  const exports = useDataExports(permitted);
  const latest = exports.data?.[0];
  const running = latest !== undefined && ACTIVE.has(latest.status);
  const start = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/data-exports")),
    onSuccess: async () => {
      toast.info(t("settings.privacy.exportStarted", { email: me.data?.user.email ?? "" }));
      await invalidate(wsKeys.dataExports(wsId));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  return (
    <section className="card card-body" aria-labelledby="export-title">
      <h2 id="export-title" className="card-title">
        {t("settings.privacy.exportTitle")}
      </h2>
      <p className="mt-1 text-xs text-fg-muted">{t("settings.privacy.exportBody")}</p>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Gate reason={manage.reason}>
          <Button variant="secondary" disabled={!manage.allowed || running} loading={start.isPending} onClick={() => { start.mutate(); }}>
            <Icon name="download" />
            {t("settings.privacy.exportAction")}
          </Button>
        </Gate>
        {exports.isPending && permitted ? <Skeleton className="h-5 w-[160px]" /> : latest ? <ExportStatus item={latest} /> : null}
      </div>
    </section>
  );
}
